import React, { forwardRef, type Ref, useCallback, useContext, useEffect, useState } from 'react';

import {
  AccountFormControls,
  AccountPasswordInput,
  Box,
  Button,
  Link,
  NameInput,
  PhoneInput,
  SimpleCheckbox,
  Text,
  type TextInputProps,
} from '@opendoor/bricks-next';
import type { TextInput as RNTextInput } from 'react-native';

import { NamelessAccountContext } from './NamelessAccountContext';
import {
  Disclaimer,
  type DisclaimerProps,
  EmailDisclaimerText,
  Form,
  MARKETING_SMS_CONSENT,
} from './shared';

export interface IAccountPasswordFormProps {
  /**
   * Name of the account form in analytics events, should be unique within the app
   * Convention: {appName}-{accountFormName}
   */
  analyticsName: string;
  email: string;
  name?: string;
  phone?: string;
  loading?: boolean;
  loginError?: string;
  isNewHuman: boolean;
  onLogin: (password: string) => void;
  onRegister: (password: string, name: string, phone: string) => void;
  phoneNumberInputOptional?: boolean;
  phoneNumberInputHidden?: boolean;
  openForgotPasswordInNewTab?: boolean;
  showPasswordlessLogin?: boolean;
  onRequestPasswordlessLogin?: () => void;
  isRequestingPasswordlessLogin?: boolean;
  hidePasswordField?: boolean;
  onboardingAccountPageCTA?: boolean;
  fullNamePlaceholder?: string;
  fullNameLabel?: string;
  disclaimerProps?: DisclaimerProps;
  passwordFormActionText?: string;
  fullNameOptional?: boolean;
  fullNameHidden?: boolean;
}

const AccountPasswordForm = forwardRef(
  (
    {
      name: initialName,
      phone: initialPhone,
      onRegister,
      onLogin,
      hidePasswordField,
      isNewHuman,
      onboardingAccountPageCTA,
      passwordFormActionText,
      analyticsName,
      fullNameLabel,
      fullNamePlaceholder,
      loading,
      loginError,
      phoneNumberInputOptional = true,
      phoneNumberInputHidden = false,
      showPasswordlessLogin,
      onRequestPasswordlessLogin,
      isRequestingPasswordlessLogin,
      openForgotPasswordInNewTab,
      fullNameOptional,
      fullNameHidden,
    }: IAccountPasswordFormProps,
    ref: Ref<RNTextInput>,
  ) => {
    const context = useContext(NamelessAccountContext);

    const [hasError, setHasError] = useState<boolean>(false);
    const [name, setName] = useState<string>(initialName || '');
    const [hasNameError, setNameError] = useState<boolean>(false);
    const [hasPasswordError, setHasPasswordError] = useState<boolean>(false);
    const [phone, setPhone] = useState<string>(initialPhone || '');
    const [smsSubscribed, setSmsSubscribed] = useState<boolean>(false);
    const [hasPhoneError, setPhoneError] = useState<boolean>(!phoneNumberInputOptional);
    const [password, setPassword] = useState<string>('');
    const [lastSubmit, setLastSubmit] = useState<{
      name?: string;
      password?: string;
      phone?: string;
    }>({});

    const getPhoneState = useCallback(
      (phone?: string) => {
        if (phone == null || phone === '') {
          return { hasError: !phoneNumberInputOptional, value: phone };
        }

        // ValidatedTextInput with format="telephoneNumber" will handle phone validation
        // This is just for initial state - actual validation happens in the component
        return {
          hasError: false, // Let ValidatedTextInput handle validation
          value: phone,
        };
      },
      [phoneNumberInputOptional],
    );

    const maybePrefillAccount = useCallback(() => {
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.searchParams);
      const phoneNumber = params.get('phoneNumber') || initialPhone;
      const { value, hasError } = getPhoneState(phoneNumber);

      if (params.get('name') || params.get('phoneNumber')) {
        setName(params.get('name') ?? '');
        setPhone(hasError ? '' : (value ?? ''));
        setPhoneError(hasError);
      }
    }, [getPhoneState, initialPhone]);

    useEffect(() => {
      maybePrefillAccount();
      if (context.onPasswordFormLoad) {
        context.onPasswordFormLoad();
      }
    }, [context, maybePrefillAccount]);

    const validatePasswordLength = (password: string) =>
      password.length >= 5 && password.length <= 128;

    const onNameChange: TextInputProps['onChangeText'] = (name: string) => {
      setName(name);
      const isNameEmpty = name == null || name === '';
      const isNameValid = name.trim() !== '';

      if (fullNameOptional) {
        setNameError(isNameEmpty ? false : !isNameValid);
      } else {
        setNameError(isNameEmpty || !isNameValid);
      }
    };

    const handlePhoneValidationChange = (validity: 'valid' | 'invalid' | 'unknown') => {
      setPhoneError(validity !== 'valid');
    };

    const onPasswordChange: TextInputProps['onChangeText'] = (password: string) => {
      //Password is validated only on blur
      setHasPasswordError(false);

      //This is to disable submit based on onchange validation
      setHasError(password == null || !validatePasswordLength(password));
      setPassword(password);
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (hasError || (isNewHuman && (hasNameError || hasPhoneError))) {
        return;
      }

      if (
        // Only check to return early if there is a previous submission
        Object.values(lastSubmit).filter(Boolean).length !== 0 &&
        name === lastSubmit.name &&
        password === lastSubmit.password &&
        phone === lastSubmit.phone
      ) {
        return;
      }

      if (isNewHuman) {
        if (context.shouldRegisterOnEmailSubmit && context.onAccountUpdate) {
          context.onAccountUpdate(name, phone, smsSubscribed);
        } else {
          // Registering on pw page does not support SMS opt-out
          onRegister(password, name, phone);
        }
      } else {
        onLogin(password);
      }

      setLastSubmit({
        name: name,
        password,
        phone: phone,
      });
    };

    const shouldDisableSubmit =
      hasError ||
      (!hidePasswordField && (!password || password.length === 0)) ||
      (isNewHuman && ((!fullNameOptional && (!name || hasNameError)) || hasPhoneError));

    let actionText = 'Confirm';
    if (phoneNumberInputHidden) {
      actionText = 'Next';
    } else if (onboardingAccountPageCTA) {
      actionText = 'Continue';
    }

    if (passwordFormActionText) {
      actionText = passwordFormActionText;
    }

    const hasPasswordRequirementError = hasPasswordError && isNewHuman,
      showPasswordError = hasPasswordRequirementError || loginError,
      passwordErrorText = loginError ? loginError : 'Your password does not meet the requirements';

    const shouldRenderDisclaimerAboveCTA = !phoneNumberInputHidden && isNewHuman;

    const shouldRenderDisclaimerBelowCTA = !shouldRenderDisclaimerAboveCTA;

    useEffect(() => {
      if (showPasswordError && passwordErrorText) {
        if (ref && 'current' in ref && ref.current) {
          ref.current.focus();
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPasswordError, passwordErrorText]);

    return (
      <Box>
        <Form onSubmitCapture={handleSubmit}>
          <Box marginBottom="$20x">
            <Box gap="$20x">
              {isNewHuman && !fullNameHidden && (
                <NameInput
                  analyticsName={analyticsName}
                  autoComplete="name"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus={true}
                  errorText={hasNameError ? 'Please enter your full name.' : undefined}
                  title={fullNameLabel ? fullNameLabel : 'Full name'}
                  onChangeText={onNameChange}
                  placeholder={fullNamePlaceholder ? fullNamePlaceholder : ''}
                  required={!fullNameOptional}
                  prefilledValue={name}
                  invalidNameError="Please enter your full name."
                  size="medium"
                />
              )}
              {!hidePasswordField && (
                <AccountPasswordInput
                  analyticsName={analyticsName}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus={!isNewHuman}
                  supportText="Your password must be 6 to 128 characters long"
                  errorText={showPasswordError && passwordErrorText}
                  onBlur={() => {
                    setHasPasswordError(password.length > 0 && !validatePasswordLength(password));
                  }}
                  title="Password"
                  ref={ref}
                  onChangeText={onPasswordChange}
                  prefilledValue={password}
                  size="medium"
                />
              )}
              {isNewHuman && !phoneNumberInputHidden && (
                <Box gap="$5x">
                  <PhoneInput
                    analyticsName={analyticsName}
                    title="Phone number"
                    onChangeText={(value: string) => setPhone(value)}
                    onValidationChange={handlePhoneValidationChange}
                    required={!phoneNumberInputOptional}
                    prefilledValue={phone}
                    invalidPhoneError="Please enter a valid phone number"
                    fieldRequiredError="This field is required"
                    size="medium"
                  />

                  {analyticsName === 'reception-fe-account-form' && (
                    <SimpleCheckbox
                      size="xsmall"
                      label={MARKETING_SMS_CONSENT.checkboxLabel}
                      value="value"
                      analyticsName={`${analyticsName}-sms-checkbox`}
                      onPress={(_, checked) => {
                        if (typeof checked === 'boolean') {
                          setSmsSubscribed(checked);
                        }
                      }}
                      name="marketing-opt-in"
                    />
                  )}
                </Box>
              )}
            </Box>
            <Box
              mt={shouldRenderDisclaimerAboveCTA ? '$0x' : '$4x'}
              $largerThanSM={{
                marginBottom: shouldRenderDisclaimerAboveCTA ? '$10x' : null,
              }}
              $smallerThanMD={{
                marginBottom: shouldRenderDisclaimerAboveCTA ? '$5x' : null,
              }}
            >
              {shouldRenderDisclaimerAboveCTA && (
                <EmailDisclaimerText analyticsName={analyticsName} />
              )}
            </Box>
            <AccountFormControls
              actionText={actionText}
              analyticsName={analyticsName}
              loading={loading}
              showForgotPassword={!isNewHuman}
              onRequestPasswordlessLogin={onRequestPasswordlessLogin}
              isRequestingPasswordlessLogin={isRequestingPasswordlessLogin}
              disabled={shouldDisableSubmit}
              submitA11yProps={{ 'aria-label': '' }}
              openForgotPasswordInNewTab={openForgotPasswordInNewTab}
              forgotPasswordText="Forgot your password?"
              forgotPasswordAriaNewWindow="Opens a new window with the forgot password page"
              forgotPasswordAriaSameWindow="Opens the forgot password page"
            />
          </Box>
        </Form>
        {showPasswordlessLogin && (
          <Box
            data-testid="passwordless-login"
            paddingTop="$20x"
            marginBottom="$20x"
            borderTopWidth={1}
            borderTopColor="$borderPrimary"
          >
            <Text typography="$subheaderSmall" tag="h3" paddingBottom="$10x" textAlign="left">
              Tired of resetting your password?
            </Text>
            <Text typography="$bodySmall" tag="h3" textAlign="left" paddingBottom="$20x">
              We can email you a link that will simply log you into your account.
            </Text>
            <Button
              variant="secondary"
              loading={isRequestingPasswordlessLogin}
              analyticsName={`${analyticsName}-email-login-link`}
              aria-label={null}
              onPress={onRequestPasswordlessLogin}
              size="medium"
              label="Email login link"
            />
          </Box>
        )}
        {shouldRenderDisclaimerBelowCTA && (
          <Disclaimer mt={5}>
            Your information is protected by our&nbsp;
            <Link
              href="https://www.opendoor.com/w/privacy"
              target="_blank"
              analyticsName={`${analyticsName}-privacy-policy`}
              aria-label="Opens a new page with Opendoor’s privacy policy"
              typography="$bodyXsmall"
              onPress={() => {}}
            >
              Privacy Policy
            </Link>
            .<br></br>
            <br></br>
          </Disclaimer>
        )}
      </Box>
    );
  },
);

AccountPasswordForm.displayName = 'AccountPasswordForm';
export default AccountPasswordForm;
